import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tablesDB, DB_ID, PRODUCTS_TABLE, account } from "../../appwrite";
import { Query } from "appwrite";
import { Pencil, Search, PackagePlus } from "lucide-react";
import { toast } from "react-toastify";
import "./AllProduct.css";
import { useTitle } from "../../hooks/useTitle";

const PER_PAGE = 10;

const fmtCategory = (slug) =>
  (slug || "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const fmtPrice = (price) =>
  "₦" +
  parseFloat(price || 0).toLocaleString("en-NG", { minimumFractionDigits: 0 });

export default function MyVault() {
  useTitle("My Vault - TrendBay Seller");
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const meta = document.querySelector("meta[name='theme-color']");
    const orig = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#080F0D");
    return () => {
      if (meta) meta.setAttribute("content", orig);
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const user = await account.get();
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          queries: [
            Query.equal("sellerId", user.$id),
            Query.orderDesc("$createdAt"),
            Query.limit(500),
          ],
        });
        const rows = (res.rows || []).map((r) => ({ id: r.$id, ...r }));
        setProducts(rows);
        setFiltered(rows);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const q = query.toLowerCase();
    const result = products.filter(
      (p) =>
        (p.productName || "").toLowerCase().includes(q) ||
        (p.category || "").toLowerCase().includes(q),
    );
    setFiltered(result);
    setPage(1);
  }, [query, products]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const slice = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="alp-page">
      <div className="alp-header">
        <div>
          <h1 className="alp-title">My Vault</h1>
          <p className="alp-subtitle">
            {filtered.length} listing{filtered.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          className="alp-add-btn"
          onClick={() => navigate("/s/add-product")}
        >
          <PackagePlus size={16} /> Add Product
        </button>
      </div>

      <div className="alp-search-wrap">
        <Search size={15} className="alp-search-icon" />
        <input
          className="alp-search"
          placeholder="Search your listings…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="alp-table-wrap">
        {loading ? (
          <p className="alp-loading">Loading…</p>
        ) : slice.length === 0 ? (
          <p className="alp-empty">No listings yet. Add your first product!</p>
        ) : (
          <table className="alp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Category</th>
                <th>Condition</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {slice.map((product, i) => (
                <tr key={product.id}>
                  <td className="alp-td-num">
                    {(page - 1) * PER_PAGE + i + 1}
                  </td>
                  <td>
                    <div className="alp-product-cell">
                      <img
                        className="alp-thumb"
                        src={product.productImage}
                        alt={product.productName}
                      />
                      <span className="alp-product-name">
                        {product.productName}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className="alp-cat-badge">
                      {fmtCategory(product.category)}
                    </span>
                  </td>
                  <td>
                    <span className="alp-cat-badge">
                      {fmtCategory(product.productCondition) || "-"}
                    </span>
                  </td>
                  <td className="alp-price">{fmtPrice(product.price)}</td>
                  <td>
                    {product.sold ? (
                      <span className="alp-cat-badge" style={{ color: "#888" }}>
                        Sold
                      </span>
                    ) : product.available ? (
                      <span
                        className="alp-cat-badge"
                        style={{ color: "#00A855", background: "#f0fdf6" }}
                      >
                        Live
                      </span>
                    ) : (
                      <span className="alp-cat-badge" style={{ color: "#aaa" }}>
                        Hidden
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="alp-actions">
                      {!product.sold ? (
                        <button
                          className="alp-btn-edit"
                          onClick={() =>
                            navigate(`/s/edit-product/${product.id}`)
                          }
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                      ) : (
                        <span className="alp-sku">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards */}
      <div className="alp-mobile-cards">
        {!loading &&
          slice.map((product) => (
            <div className="alp-mobile-card" key={product.id}>
              <div className="alp-mobile-card__main">
                <img
                  className="alp-thumb"
                  src={product.productImage}
                  alt={product.productName}
                />
                <div className="alp-mobile-card__info">
                  <p className="alp-mobile-card__name">{product.productName}</p>
                  <span className="alp-cat-badge">
                    {fmtCategory(product.category)}
                  </span>
                </div>
                <span className="alp-price">{fmtPrice(product.price)}</span>
              </div>
              <div className="alp-mobile-card__footer">
                <span className="alp-sku">
                  {fmtCategory(product.productCondition) || "-"}
                </span>
                <div className="alp-actions">
                  {!product.sold ? (
                    <button
                      className="alp-btn-edit"
                      onClick={() => navigate(`/s/edit-product/${product.id}`)}
                    >
                      <Pencil size={15} />
                    </button>
                  ) : (
                    <span className="alp-sku">Sold</span>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      {totalPages > 1 && (
        <div className="alp-pagination">
          <button
            className="alp-page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span className="alp-page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="alp-page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
